import { Flex, Box, Text } from "@chakra-ui/react"
import { useState } from "react"
import { Control, FieldValues, UseFormSetValue, useWatch } from "react-hook-form"
import { useParams } from "react-router-dom"
import { useAppContext } from "../contexts/AppProvider"
import { useProjectsContext } from "../contexts/ProjectsProvider"

import { IFormField } from "../interfaces/IFormField"
import { IFormIteration } from "../interfaces/IFormIteration"
import { IFormRow } from "../models/reportform"
import { isPermitted } from "./Can"
import Field from "./FormControls/Field"

/** Furnace Lab loads furnace-closure from JSON (not DB); show intro text above this section title only. */
const isFurnaceClosureForm = (form: { icon?: string; name?: string } | null): boolean =>
  !!form &&
  (form.icon === "FurnaceClosureIcon" ||
    (form.name ?? "").toLowerCase().includes("furnace closure"))

const isBlankControllingValue = (value: unknown): boolean =>
  value == null || value === ""

const TemplateSection = ({
  name,
  description,
  rows,
  formIteration,
  formIterationNumber = 0,
  savedValues,
  control,
  setValue,
  canEditForm,
}: {
  name: string
  description: string
  rows: IFormRow[]
  formIteration?: IFormIteration
  formIterationNumber?: number
  savedValues?: any
  control?: Control<FieldValues, any>
  setValue?: UseFormSetValue<FieldValues>
  /** When set (project form context), fields use this instead of projectTemplates.edit */
  canEditForm?: boolean
}) => {
  const { user } = useAppContext()
  const { currentProjectTemplate, currentProjectForm } = useProjectsContext()
  const [disabledFields, setDisabledFields] = useState<Record<string, string>>({})
  const { page } = useParams<{ page: string }>()
  const watchedValues = useWatch({ control: control as Control<FieldValues>, disabled: !control })

  const isPermittedToEditTemplate = isPermitted({
    user,
    action: "projectTemplates.edit",
    data: { projectTemplate: currentProjectTemplate },
  })
  const isFormCompleted = formIteration?.status === "completed"
  const fieldDisabled = setValue
    ? isFormCompleted || !(canEditForm ?? false)
    : isFormCompleted || !isPermittedToEditTemplate

  const getCurrentFieldValue = (key: string) => {
    if (control && watchedValues && typeof watchedValues === "object") {
      return (watchedValues as Record<string, unknown>)[key]
    }
    if (savedValues?.current) {
      return savedValues.current[key]
    }
    return formIteration?.values?.[key]
  }

  const descriptionAboveTitle =
    Boolean(description) &&
    isFurnaceClosureForm(currentProjectForm) &&
    (name?.trim() === "Header Information" || name?.trim() === "1. Horizontal Furnace")

  const hideSectionHeading =
    name?.trim() === "Header Information" || name?.trim() === "Header & equipment"

  const descriptionBlock = description ? (
    <Text
      fontSize="smm"
      fontWeight="semi_medium"
      color="breNavy"
      mt={descriptionAboveTitle ? "0" : "10px"}
      mb="20px"
    >
      {description}
    </Text>
  ) : null

  const fieldVisibility = (rows || []).flatMap((row) =>
    (row.fields || []).map((field) => {
      const requiredWhen = field.requiredWhen || {}
      const requiredWhenKeys = Object.keys(requiredWhen)
      const hasRequiredWhen = requiredWhenKeys.length > 0
      const controllingValuesSet =
        hasRequiredWhen &&
        requiredWhenKeys.every((key) => !isBlankControllingValue(getCurrentFieldValue(key)))

      let shouldEnableField = true
      if (hasRequiredWhen) {
        // Hide gated fields until the user makes a selection — do not wipe values yet.
        if (!controllingValuesSet) {
          shouldEnableField = false
        } else {
          shouldEnableField = requiredWhenKeys.every(
            (key) => getCurrentFieldValue(key) === requiredWhen[key]
          )
        }
      }
      return { field, hasRequiredWhen, controllingValuesSet, shouldEnableField }
    })
  )

  const hasGatedFields = fieldVisibility.some(({ hasRequiredWhen }) => hasRequiredWhen)
  const allFieldsAreGated =
    fieldVisibility.length > 0 && fieldVisibility.every(({ hasRequiredWhen }) => hasRequiredWhen)
  const anyGatedFieldEnabled = fieldVisibility.some(
    ({ hasRequiredWhen, shouldEnableField }) => hasRequiredWhen && shouldEnableField
  )
  // Hide entire furnace (or other fully gated) sections until the matching option is selected.
  if (allFieldsAreGated && !anyGatedFieldEnabled) {
    // Still clear non-matching sections once a controlling value is set (allows switching type).
    if (setValue) {
      for (const { field, hasRequiredWhen, controllingValuesSet, shouldEnableField } of fieldVisibility) {
        if (!hasRequiredWhen || !controllingValuesSet) continue
        if (!shouldEnableField && !disabledFields[field.name]) {
          setDisabledFields((prevState) => ({
            ...prevState,
            [field.name]: "N/A",
          }))
          setValue(field.name, "N/A")
        }
      }
    }
    return null
  }

  return (
    <Box id={name.replace(/\s/g, "").toLowerCase()}>
      {descriptionAboveTitle && descriptionBlock}
      {name && !hideSectionHeading ? (
        <Text fontSize="md" fontWeight="bold" color="breNavy">
          {name}
        </Text>
      ) : (
        ""
      )}
      {!descriptionAboveTitle && descriptionBlock}
      <Box>
        {(rows || []).map((row: IFormRow, i) => (
          <Flex
            key={row._id != null ? row._id + formIterationNumber : `row-${i}-${formIterationNumber}`}
            justify="flex-start"
            align="flex-start"
            width="full"
          >
            {(row.fields || []).map((field: IFormField, j) => {
              const requiredWhen = field.requiredWhen || {}
              const requiredWhenKeys = Object.keys(requiredWhen)
              const hasRequiredWhen = requiredWhenKeys.length > 0
              const controllingValuesSet =
                hasRequiredWhen &&
                requiredWhenKeys.every((key) => !isBlankControllingValue(getCurrentFieldValue(key)))

              let shouldEnableField = true
              if (hasRequiredWhen) {
                if (!controllingValuesSet) {
                  shouldEnableField = false
                } else {
                  shouldEnableField = requiredWhenKeys.every(
                    (key) => getCurrentFieldValue(key) === requiredWhen[key]
                  )
                }
              }

              if (setValue && hasRequiredWhen && controllingValuesSet) {
                // Only clear/N/A non-selected sections after a furnace type is chosen,
                // so switching type is allowed without destroying data before selection.
                if (!shouldEnableField && !disabledFields[field.name]) {
                  setDisabledFields((prevState) => ({
                    ...prevState,
                    [field.name]: "N/A",
                  }))
                  setValue(field.name, "N/A")
                }

                if (
                  shouldEnableField &&
                  disabledFields[field.name] &&
                  disabledFields[field.name] !== ""
                ) {
                  setDisabledFields((prevState) => ({
                    ...prevState,
                    [field.name]: "",
                  }))
                  setValue(field.name, "")
                }
              }

              return (
                <Box
                  mb="10px"
                  mr="15px"
                  width={
                    field.style?.width != null && field.style?.width !== ""
                      ? field.style.width
                      : (row.fields || []).length > 1
                        ? "360px"
                        : "full"
                  }
                  minH={field.type === "staticText" ? "0px" : "90px"}
                  key={field._id != null ? field._id + formIterationNumber : `field-${j}-${formIterationNumber}`}
                  display={shouldEnableField ? "" : "none"}
                >
                  <Field
                    control={control}
                    field={{
                      ...field,
                      disabled: fieldDisabled,
                      placeholder:
                        field.type === "dropdown"
                          ? "Select"
                          : field.placeholder,
                    }}
                    formIndex={page}
                  />
                </Box>
              )
            })}
            {}
          </Flex>
        ))}
      </Box>
    </Box>
  )
}

export default TemplateSection
